export const TEAM_ROLE_VIEW_ONLY = 1;
export const TEAM_ROLE_CONTENT_MANAGER = 2;
export const TEAM_ROLE_ADMIN = 3;

export const TEAM_ROLE_LABELS = {
  [TEAM_ROLE_VIEW_ONLY]: "View-Only",
  [TEAM_ROLE_CONTENT_MANAGER]: "Content Manager",
  [TEAM_ROLE_ADMIN]: "Admin"
};

export const TEAM_ROLE_DETAILS = {
  [TEAM_ROLE_VIEW_ONLY]: {
    description: "Read-only access for monitoring and reporting",
    canDo: [
      "View all listings and listing details",
      "Read all inbox messages and conversations",
      "View referral requests (sent and received)",
      "View business referrals",
      "View questions and answers",
      "View performance analytics (leads, traffic and geography figures)",
      "View payment history and download invoices (PDF)",
      "See lead matching indicators",
      "View pending listing versions"
    ],
    cannotDo: [
      "Edit any content or listings",
      "Send messages or respond to inquiries",
      "Make purchases or payment changes",
      "Modify account settings",
      "Accept pro bono cases",
      "Purchase paid referrals",
      "Submit listings for review"
    ]
  },
  [TEAM_ROLE_CONTENT_MANAGER]: {
    description: "Day-to-day operational management including full listing management",
    canDo: [
      "All View-Only permissions",
      "Full listing editing (same as owner)",
      "Edit pending versions while under review",
      "Submit and resubmit listings for review",
      "Create new content sets for multiple languages",
      "Manage inbox (send and reply)",
      "Respond to referral requests",
      "Handle incoming leads"
    ],
    cannotDo: [
      "Purchase business referrals",
      "Accept pro bono cases",
      "Modify payment methods",
      "Add or remove team members",
      "Change account-level settings (owner name, email, phone, location)",
      "Delete listings or the account",
      "Upgrade or downgrade sponsor status"
    ]
  },
  [TEAM_ROLE_ADMIN]: {
    description: "Full operational control with financial access",
    canDo: [
      "All Content Manager permissions (full listing edit, pending versions, submit/resubmit, multi-language content, rejection fixes)",
      "Upgrade, downgrade, renew, pause/unpause, and delete non-profile listings",
      "Purchase business referrals (charged to owner)",
      "Accept pro bono cases (counts against owner's monthly limit)",
      "Manage payment methods (add/update)",
      "View and manage financial transactions",
      "Download invoices and reports"
    ],
    cannotDo: [
      "Delete the owner's Profile listing",
      "Remove the account owner",
      "Add or remove other team members",
      "Delete the owner's account",
      "Modify account-level owner information",
      "Transfer account ownership",
      "Approve listings or skip review",
      "See other team members"
    ]
  }
};

/** Profile listings cannot be deleted by anyone (owner or team). */
export function isProfileListingType(listingTypeOrProfession) {
  const value = String(listingTypeOrProfession || "").toLowerCase();
  return value.includes("profile") || value.includes("people") || value === "profiles";
}

/**
 * @param {Object} params
 * @param {string|number|null} params.profileUserId
 * @param {string|number|null} params.ownerId
 * @param {number|null} params.role
 */
export function resolveTeamPermissions({ profileUserId, ownerId, role }) {
  const ownerIdStr = ownerId != null && ownerId !== "" ? String(ownerId) : null;
  const profileIdStr = profileUserId != null && profileUserId !== "" ? String(profileUserId) : null;
  const isOwnAccount = Boolean(profileIdStr && ownerIdStr && ownerIdStr === profileIdStr);
  // Non-empty ownerId is team context until profile proves it is the member's own id
  const isTeamContext = Boolean(ownerIdStr && !isOwnAccount);
  const isOwnerContext = !isTeamContext;
  const teamRole = isTeamContext && role != null ? Number(role) : null;
  const isRolePending = isTeamContext && teamRole == null;

  const hasRoleAtLeast = minRole => {
    if (isRolePending) {
      return false;
    }
    return isOwnerContext || (teamRole != null && teamRole >= minRole);
  };

  return {
    isTeamContext,
    isOwnerContext,
    isRolePending,
    ownerId: ownerIdStr,
    role: teamRole,
    roleLabel: teamRole != null ? TEAM_ROLE_LABELS[teamRole] : null,
    isViewOnly: isTeamContext && teamRole === TEAM_ROLE_VIEW_ONLY,
    isContentManager: isTeamContext && teamRole === TEAM_ROLE_CONTENT_MANAGER,
    isAdmin: isTeamContext && teamRole === TEAM_ROLE_ADMIN,
    canViewListings: true,
    canViewPendingVersions: isOwnerContext || isTeamContext,
    canEditListings: hasRoleAtLeast(TEAM_ROLE_CONTENT_MANAGER),
    canEditPendingVersions: hasRoleAtLeast(TEAM_ROLE_CONTENT_MANAGER),
    canManageMultiLanguageContent: hasRoleAtLeast(TEAM_ROLE_CONTENT_MANAGER),
    canHandleListingRejection: hasRoleAtLeast(TEAM_ROLE_CONTENT_MANAGER),
    canSendMessages: hasRoleAtLeast(TEAM_ROLE_CONTENT_MANAGER),
    canSubmitForReview: hasRoleAtLeast(TEAM_ROLE_CONTENT_MANAGER),
    /** Upgrade, downgrade, renew, cancel (json-api ListingController adminActions) */
    canManageListingFinancials: hasRoleAtLeast(TEAM_ROLE_ADMIN),
    /** Pause / unpause (json-api ListingController contentManagerActions) */
    canPauseUnpauseListing: hasRoleAtLeast(TEAM_ROLE_CONTENT_MANAGER),
    /** Delete org/event/job listings only — never Profile */
    canDeleteListings: hasRoleAtLeast(TEAM_ROLE_ADMIN),
    canRespondToReferrals: hasRoleAtLeast(TEAM_ROLE_CONTENT_MANAGER),
    canPostQa: hasRoleAtLeast(TEAM_ROLE_CONTENT_MANAGER),
    canManagePayments: hasRoleAtLeast(TEAM_ROLE_ADMIN),
    canPurchaseReferrals: hasRoleAtLeast(TEAM_ROLE_ADMIN),
    canAcceptProBono: hasRoleAtLeast(TEAM_ROLE_ADMIN),
    canManageTeam: isOwnerContext,
    canSeeTeamMembers: isOwnerContext,
    canEditAccountSettings: isOwnerContext
  };
}

/**
 * Whether delete is allowed for this listing type in the current team context.
 *
 * @param {ReturnType<typeof resolveTeamPermissions>} permissions
 * @param {string} [listingTypeOrProfession]
 */
export function canDeleteListing(permissions, listingTypeOrProfession) {
  if (!permissions?.canDeleteListings) {
    return false;
  }

  return !isProfileListingType(listingTypeOrProfession);
}

export function findMemberAccess(memberList, ownerId, { activeOnly = false } = {}) {
  if (!memberList?.length || ownerId == null) {
    return null;
  }

  return (
    memberList.find(item => {
      if (String(item.owner_id) !== String(ownerId)) {
        return false;
      }
      if (activeOnly && String(item.status) !== "1") {
        return false;
      }
      return true;
    }) || null
  );
}

export function findActiveMemberAccess(memberList, ownerId) {
  return findMemberAccess(memberList, ownerId, { activeOnly: true });
}

/**
 * @param {Object} profile
 * @param {string} [fallback] Translated own-account label for a profile with no name and no email (the helper has no
 * `t`, so the caller supplies it: `t("dashboard_common_selectteam_myaccount")`).
 */
export function getProfileDisplayName(profile = {}, fallback = "") {
  const name = [profile.name_f, profile.name_l]
    .filter(Boolean)
    .join(" ")
    .trim();

  return name || profile.email || fallback;
}

export function getAccessibleTeamMembers(memberList = []) {
  return memberList.filter(item => String(item.status) === "1");
}

export function buildTeamSelection(memberItem) {
  if (!memberItem) {
    return { value: "", label: "", role: null };
  }
  return {
    value: String(memberItem.owner_id),
    label: memberItem.name_f || memberItem.name_l ? `${memberItem.name_f} ${memberItem.name_l}`.trim() : memberItem.member_name,
    role: Number(memberItem.role)
  };
}

/**
 * Dropdown options: own account first, then accepted team owner accounts.
 *
 * @param {Object} profile
 * @param {Array} memberList
 * @param {string} [ownAccountLabel] Page-specific own-account label (e.g. "My Leads"). Falls back to the profile name.
 * @returns {Array<{ value: string, label: string, role: number|null, isOwnAccount?: boolean }>}
 */
export function buildTeamAccountSwitcherOptions(profile = {}, memberList = [], ownAccountLabel) {
  const ownAccount = {
    value: "",
    label: ownAccountLabel || getProfileDisplayName(profile),
    role: null,
    isOwnAccount: true
  };
  const accessible = getAccessibleTeamMembers(memberList);

  if (accessible.length) {
    return [ownAccount, ...accessible.map(buildTeamSelection)];
  }

  // Member has team rows but none active yet — still show own account in the dropdown
  if (memberList?.length) {
    return [ownAccount];
  }

  return [];
}

export function getAccountContextLabel(profile = {}, currentTeam = {}, memberList = [], fallbackLabel) {
  if (!currentTeam?.value) {
    return getProfileDisplayName(profile, fallbackLabel);
  }

  const access = findActiveMemberAccess(memberList, currentTeam.value);

  if (access) {
    return buildTeamSelection(access).label;
  }

  return currentTeam.label || getProfileDisplayName(profile, fallbackLabel);
}

export function getTeamOwnerPaymentNotice(permissions) {
  if (!permissions?.isTeamContext) {
    return null;
  }

  return "Purchases are charged to the account owner's payment method. Invoices are sent to the account owner.";
}

export function getTeamAccessBannerMessage(permissions, accountLabel = "") {
  if (!permissions.isTeamContext) {
    return null;
  }

  const viewing = accountLabel ? `Currently viewing ${accountLabel}'s account` : "Currently viewing a team account";

  if (permissions.isRolePending) {
    return `${viewing}. Your access level is loading — editing is disabled until it is confirmed.`;
  }

  if (!permissions.roleLabel) {
    return null;
  }

  if (permissions.isViewOnly) {
    return `${viewing} as ${permissions.roleLabel}. You can view content but cannot make changes.`;
  }
  if (permissions.isContentManager) {
    return `${viewing} as ${permissions.roleLabel}. Payment and referral purchases are not available for your role.`;
  }
  if (permissions.isAdmin) {
    return `${viewing} as ${permissions.roleLabel}. Team management and owner account settings are not available for your role.`;
  }

  return `${viewing} as ${permissions.roleLabel}.`;
}
